<mat-sidenav-container class="h-screen w-full" autosize>
  <mat-sidenav #sidenav mode="over" class="w-64 p-4">
    <div class="flex flex-col h-full">
      <!-- User Section -->
      <div class="mb-6 p-2 border-b border-slate-200 dark:border-slate-700">
        @if (auth.isAuthInitializing()) {
          <!-- Skeleton card while initializing -->
          <div class="flex items-center gap-3 animate-pulse">
            <div class="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700"></div>
            <div class="flex flex-col gap-2 flex-1">
              <div class="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3"></div>
              <div class="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
            </div>
          </div>
        } @else if (auth.currentUser(); as user) {
          <div class="flex items-center gap-3">
            @if (user.photoURL) {
            <img [src]="user.photoURL" alt="Profile" class="w-10 h-10 rounded-full border border-slate-200 dark:border-slate-700">
            } @else {
            <div class="avatar-fallback uppercase w-10 h-10">
              {{ user.displayName?.charAt(0) ?? 'P' }}
            </div>
            }
            <div class="flex flex-col overflow-hidden">
              <span class="font-semibold truncate">{{ user.displayName ?? 'User' }}</span>
              <span class="text-xs text-slate-500 truncate">{{ user.email }}</span>
            </div>
          </div>
        } @else {
          <button mat-button class="w-full text-left" (click)="auth.onGoogleSignIn(); sidenav.close()">
            <mat-icon class="mr-2">login</mat-icon>
            Sign In with Google
          </button>
        }
      </div>

      <!-- Navigation Links -->
      <mat-nav-list class="flex-1">
        <a mat-list-item [routerLink]="['/home']" (click)="sidenav.close()" routerLinkActive="active-link">
          <mat-icon matListItemIcon>home</mat-icon>
          <span matListItemTitle>Home</span>
        </a>
        <a mat-list-item [routerLink]="['/locations']" (click)="sidenav.close()" routerLinkActive="active-link">
          <mat-icon matListItemIcon>place</mat-icon>
          <span matListItemTitle>Locations</span>
        </a>
        <a mat-list-item [routerLink]="['/playgrounds']" (click)="sidenav.close()" routerLinkActive="active-link">
          <mat-icon matListItemIcon>park</mat-icon>
          <span matListItemTitle>Playgrounds</span>
        </a>
        <a mat-list-item [routerLink]="['/day-trips']" (click)="sidenav.close()" routerLinkActive="active-link">
          <mat-icon matListItemIcon>map</mat-icon>
          <span matListItemTitle>Day Trips</span>
        </a>
        @if (auth.isAuthInitializing()) {
          <!-- Skeleton links to reserve layout space -->
          <div class="px-4 py-3 animate-pulse flex items-center gap-6">
            <div class="w-6 h-6 rounded bg-slate-200 dark:bg-slate-700"></div>
            <div class="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24"></div>
          </div>
          <div class="px-4 py-3 animate-pulse flex items-center gap-6">
            <div class="w-6 h-6 rounded bg-slate-200 dark:bg-slate-700"></div>
            <div class="h-4 bg-slate-200 dark:bg-slate-700 rounded w-16"></div>
          </div>
        } @else if (auth.currentUser()) {
          <a mat-list-item [routerLink]="['/scheduler']" (click)="sidenav.close()" routerLinkActive="active-link">
            <mat-icon matListItemIcon>calendar_month</mat-icon>
            <span matListItemTitle>Scheduler</span>
          </a>
          <a mat-list-item [routerLink]="['/profile']" (click)="sidenav.close()" routerLinkActive="active-link">
            <mat-icon matListItemIcon>person</mat-icon>
            <span matListItemTitle>Profile</span>
          </a>
        }
        @if (adminQueueResource.value()?.isAdmin) {
        <a mat-list-item [routerLink]="['/admin']" (click)="sidenav.close()" routerLinkActive="active-link">
          <mat-icon matListItemIcon>admin_panel_settings</mat-icon>
          <span matListItemTitle>Admin</span>
        </a>
        <a mat-list-item [routerLink]="['/playgrounds/moderation']" (click)="sidenav.close()" routerLinkActive="active-link">
          <mat-icon matListItemIcon [matBadge]="adminQueueResource.value()?.pendingPlaygroundCount ?? 0"
            [matBadgeHidden]="(adminQueueResource.value()?.pendingPlaygroundCount ?? 0) === 0" matBadgeColor="warn">
            gavel
          </mat-icon>
          <span matListItemTitle>Playground Reviews</span>
        </a>
        <a mat-list-item [routerLink]="['/playgroups/moderation']" (click)="sidenav.close()" routerLinkActive="active-link">
          <mat-icon matListItemIcon [matBadge]="adminQueueResource.value()?.pendingPlaygroupCount ?? 0"
            [matBadgeHidden]="(adminQueueResource.value()?.pendingPlaygroupCount ?? 0) === 0" matBadgeColor="warn">
            groups
          </mat-icon>
          <span matListItemTitle>Playgroup Reviews</span>
        </a>
        <a mat-list-item [routerLink]="['/day-trips/moderation']" (click)="sidenav.close()" routerLinkActive="active-link">
          <mat-icon matListItemIcon [matBadge]="adminQueueResource.value()?.pendingDayTripCount ?? 0"
            [matBadgeHidden]="(adminQueueResource.value()?.pendingDayTripCount ?? 0) === 0" matBadgeColor="warn">
            rate_review
          </mat-icon>
          <span matListItemTitle>Day Trip Reviews</span>
        </a>
        }
      </mat-nav-list>

      <!-- Bottom actions -->
      <div class="mt-auto pt-4 border-t border-slate-200 dark:border-slate-700">
        <button mat-button class="w-full text-left" (click)="themeService.toggleTheme()">
          <mat-icon class="mr-2">{{ themeService.theme() === 'dark' ? 'light_mode' : 'dark_mode' }}</mat-icon>
          {{ themeService.theme() === 'dark' ? 'Light Mode' : 'Dark Mode' }}
        </button>
      </div>
    </div>
  </mat-sidenav>

  <mat-sidenav-content>
    @if (!isChromeHiddenRoute()) {
    <mat-toolbar class="flex flex-row sticky top-0 z-10">
      <button mat-icon-button (click)="sidenav.toggle()" class="mr-2">
        <mat-icon>menu</mat-icon>
      </button>
      <div class="font-semibold text-xl" [routerLink]="['/home']" style="cursor: pointer;">
        Playble
      </div>
      <div class="flex-1"></div>

      @if (auth.isAuthInitializing()) {
        <!-- Skeleton avatar to reserve space and prevent layout shift/button flash -->
        <div class="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse mr-2"></div>
      } @else if (auth.currentUser(); as user) {
        <button mat-icon-button class="profile-button" [routerLink]="['/profile']"
          [matTooltip]="adminQueueResource.value()?.isAdmin ? 'Admin' : 'Open profile options'">
          @if (user.photoURL) {
          <img [src]="user.photoURL" alt="Profile" class="w-8 h-8 rounded-full border border-slate-200 dark:border-slate-700"
            [class.admin-avatar-ring]="adminQueueResource.value()?.isAdmin">
          } @else {
          <div class="avatar-fallback uppercase w-8 h-8 text-xs" [class.admin-avatar-ring]="adminQueueResource.value()?.isAdmin">
            {{ user.displayName?.charAt(0) ?? 'P' }}
          </div>
          }
        </button>
      } @else {
        @if (auth.isLoading()) {
          <mat-spinner diameter="30" class="mr-4"></mat-spinner>
        } @else {
          <button mat-button (click)="auth.onGoogleSignIn()">
            <mat-icon>login</mat-icon>
            <span class="hidden sm:inline ml-1">Sign In</span>
          </button>
        }
      }
    </mat-toolbar>
    }

    <div class="w-full min-h-[calc(100vh-64px)] pb-16">
      <router-outlet></router-outlet>
    </div>

    @if (!isChromeHiddenRoute()) {
    <footer class="app-footer">
      <span>Made with ❤️, ☕️, and 🍩 by </span>
      <a href="#" (click)="openContactDialog($event)" class="footer-link">Lewis</a>
      <span>.</span>
    </footer>
    }
  </mat-sidenav-content>
</mat-sidenav-container>

@if (updateService.updateAvailable()) {
<div class="fixed bottom-0 left-0 right-0 bg-blue-600 text-white p-4 flex items-center justify-between z-50">
  <span>A new version of Playble is available.</span>
  <button mat-flat-button color="accent" (click)="updateService.applyUpdate()">
    Update now
  </button>
</div>
}
